// De las filas que manda la API (hechos) a los grupos con su tabla (derivado).
// La usan la web, para mostrar, y el Worker, para el sorteo de desempate: una
// sola cuenta, así la pantalla y la API no pueden decir cosas distintas.

import { tablaDelGrupo, type PartidoDeGrupo, type Tabla } from './grupos';
import type { Marcador } from './marcador';

/** Un partido como lo guarda la base (y como viaja en la API). */
export interface PartidoGuardado {
  id: number;
  grupo_id: number | null;
  orden: number;
  pareja_a: number | null;
  pareja_b: number | null;
  estado: 'pendiente' | 'jugado';
  set1_a: number | null;
  set1_b: number | null;
  set2_a: number | null;
  set2_b: number | null;
  tb_a: number | null;
  tb_b: number | null;
  wo: 'a' | 'b' | null;
  // Programación (etapa 4). Todo null = sin horario.
  dia: string | null;
  hora: string | null;
  cancha: number | null;
  /** Segundos unix desde que el organizador lo marcó "en juego"; null = no está en cancha. */
  en_juego_desde: number | null;
}

export function marcadorDeFila(p: PartidoGuardado): Marcador | null {
  if (p.estado !== 'jugado') return null;
  if (p.wo) return { wo: p.wo, sets: null, tb: null };
  return {
    wo: null,
    sets: [
      [p.set1_a ?? 0, p.set1_b ?? 0],
      [p.set2_a ?? 0, p.set2_b ?? 0],
    ],
    tb: p.tb_a === null || p.tb_b === null ? null : [p.tb_a, p.tb_b],
  };
}

export interface GrupoGuardado {
  id: number;
  letra: string;
  /** ids de pareja, en su posición dentro del grupo */
  parejas: number[];
  partidos: PartidoGuardado[];
  /** pareja_id -> orden del sorteo de desempate */
  sorteo: Record<number, number>;
}

export interface GrupoConTabla extends GrupoGuardado {
  tabla: Tabla;
  /** El partido con su marcador efectivo: el cargado, o el W.O. por abandono. */
  resultados: Map<number, { marcador: Marcador | null; porAbandono: boolean }>;
}

/**
 * `dadasDeBaja`: parejas de baja. Si estaban en el grupo, abandonaron: lo que
 * les falta jugar es W.O. para el rival (lo calcula tablaDelGrupo).
 */
export function conTabla(g: GrupoGuardado, dadasDeBaja: ReadonlySet<number>, pasan: number): GrupoConTabla {
  const partidos: PartidoDeGrupo[] = g.partidos
    .filter((p) => p.pareja_a !== null && p.pareja_b !== null)
    .map((p) => ({ id: p.id, a: p.pareja_a!, b: p.pareja_b!, marcador: marcadorDeFila(p) }));
  const tabla = tablaDelGrupo(
    g.parejas.map((id) => ({ id, abandono: dadasDeBaja.has(id) })),
    partidos,
    pasan,
    new Map(Object.entries(g.sorteo).map(([k, v]) => [Number(k), v])),
  );
  const resultados = new Map<number, { marcador: Marcador | null; porAbandono: boolean }>();
  for (const p of partidos) {
    if (p.marcador) resultados.set(p.id, { marcador: p.marcador, porAbandono: false });
    else {
      const aFuera = dadasDeBaja.has(p.a);
      const bFuera = dadasDeBaja.has(p.b);
      if (aFuera !== bFuera) {
        resultados.set(p.id, { marcador: { wo: aFuera ? 'b' : 'a', sets: null, tb: null }, porAbandono: true });
      } else resultados.set(p.id, { marcador: null, porAbandono: false });
    }
  }
  return { ...g, tabla, resultados };
}
